import React, { useEffect, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CANDIDATE_SOURCES, SOURCE_LABELS, INDUSTRY_NAMES } from "../lib/industries";
import { Users, Briefcase, TrendingUp, Award, Search, LogOut, BarChart3, Mail, MessageCircle, Phone, UserPlus, Star, CalendarCheck, PauseCircle, XCircle, Trophy, Send, Eye } from "lucide-react";
import JobsManager from "../components/admin/JobsManager";
import AddCandidateModal from "../components/admin/AddCandidateModal";
import CandidateProfileModal from "../components/admin/CandidateProfileModal";
import SubmitToClientModal from "../components/admin/SubmitToClientModal";
import ClientSubmissions from "../components/admin/ClientSubmissions";
import TeamAccess from "../components/admin/TeamAccess";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const STAGES = [
  { k: "new", l: "New", color: "bg-slate-100 border-slate-200" },
  { k: "shortlisted", l: "Shortlisted", color: "bg-blue-50 border-blue-200" },
  { k: "interview", l: "Interview", color: "bg-amber-50 border-amber-200" },
  { k: "selected", l: "Selected", color: "bg-teal-50 border-teal-200" },
  { k: "offered", l: "Offered", color: "bg-purple-50 border-purple-200" },
  { k: "joined", l: "Joined", color: "bg-emerald-50 border-emerald-200" },
  { k: "on_hold", l: "On Hold", color: "bg-orange-50 border-orange-200" },
  { k: "rejected", l: "Rejected", color: "bg-rose-50 border-rose-200" },
];

const CHART_COLORS = ["#0F2A4A", "#C5A059", "#1E3A8A", "#D4AF37", "#94A3B8", "#EF4444"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [q, setQ] = useState("");
  const [contacts, setContacts] = useState([]);
  const [hiringReqs, setHiringReqs] = useState([]);
  const [selectedCands, setSelectedCands] = useState([]);
  const [previewPdf, setPreviewPdf] = useState(null);
  const [interviewFor, setInterviewFor] = useState(null);
  const [interviewAt, setInterviewAt] = useState("");
  const [addCand, setAddCand] = useState(false);
  const [profileId, setProfileId] = useState(null);
  const [submitFor, setSubmitFor] = useState(null);
  const [candFilters, setCandFilters] = useState({ source: "", industry: "" });
  const [subsKey, setSubsKey] = useState(0);

  useEffect(() => { load(); }, []);
  const load = async () => {
    const [s, a, j, c, ct, h] = await Promise.all([
      api.get("/admin/stats"), api.get("/applications"),
      api.get("/my/jobs"), api.get("/candidates"),
      api.get("/contacts"), api.get("/hiring-requests")
    ]);
    setStats(s.data); setApps(a.data); setJobs(j.data);
    setCandidates(c.data); setContacts(ct.data); setHiringReqs(h.data);
  };

  const updateStatus = async (id, status, extra = {}) => {
    try {
      await api.patch(`/applications/${id}/status`, { status, ...extra });
      toast.success(`Moved to ${status}`);
      const s = await api.get("/admin/stats");
      const a = await api.get("/applications");
      setStats(s.data); setApps(a.data);
    } catch { toast.error("Failed"); }
  };

  const scheduleInterview = async () => {
    if (!interviewAt) return toast.error("Pick a date & time");
    await updateStatus(interviewFor, "interview", { interview_at: interviewAt });
    setInterviewFor(null); setInterviewAt("");
  };

  const downloadZip = async () => {
    if (selectedCands.length === 0) return toast.info("Select at least 1 candidate");
    const token = localStorage.getItem("ae_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/resumes/zip?ids=${selectedCands.join(",")}`;
    try {
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { toast.error("Download failed"); return; }
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `resumes-${new Date().toISOString().slice(0,10)}.zip`;
      a.click();
      toast.success(`Downloaded ${selectedCands.length} resumes`);
    } catch { toast.error("Download failed"); }
  };

  const sendDigest = async () => {
    try {
      const r = await api.get("/notifications/daily-digest");
      toast.success(`Digest sent — ${r.data.interviews_today} interviews · ${r.data.followups} follow-ups · ${r.data.expiring_jobs} expiring jobs`);
    } catch { toast.error("Digest failed"); }
  };

  const toggleCand = (uid) => {
    setSelectedCands(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);
  };

  const searchCands = async (f = candFilters) => {
    const params = Object.fromEntries(Object.entries({ q, ...f }).filter(([, v]) => v));
    const r = await api.get("/candidates", { params });
    setCandidates(r.data);
  };
  const setCandFilter = (k, v) => { const f = { ...candFilters, [k]: v }; setCandFilters(f); searchCands(f); };
  const refreshAll = () => { load(); setSubsKey(k => k + 1); };

  const funnelData = stats ? STAGES.filter(s => !["rejected", "on_hold"].includes(s.k)).map(s => ({ name: s.l, value: stats.funnel[s.k] || 0 })) : [];
  const pieData = stats ? Object.entries(stats.funnel).filter(([, v]) => v > 0).map(([k, v]) => ({ name: k, value: v })) : [];

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="admin-dashboard">
      <section className="bg-[#0F2A4A] text-white py-12">
        <div className="ae-container flex justify-between items-start flex-wrap gap-4">
          <div>
            <div className="ae-overline mb-2">ATS Admin Console</div>
            <h1 className="text-3xl md:text-4xl font-light">
              {user.role === "admin" ? "Admin" : "Recruiter"} · <span className="font-serif italic text-[#C5A059]">{user.full_name}</span>
            </h1>
          </div>
          <button onClick={sendDigest} data-testid="send-digest-btn" className="ae-btn-primary text-sm">📊 Send Daily Digest</button>
          <button onClick={() => { logout(); nav("/"); }} className="text-white/70 hover:text-white text-sm flex items-center gap-2" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </section>

      <div className="ae-container py-8">
        <div className="flex gap-2 mb-6 border-b border-slate-200 overflow-x-auto">
          {[
            { k: "overview", l: "Overview" },
            { k: "pipeline", l: "Pipeline" },
            { k: "candidates", l: "Candidates" },
            { k: "jobs", l: "Jobs" },
            { k: "submissions", l: "Client Submissions" },
            { k: "leads", l: "Leads" },
            ...(user.role === "admin" ? [{ k: "team", l: "Team & Access" }] : []),
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`admin-tab-${t.k}`}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${tab === t.k ? "border-[#C5A059] text-[#0F2A4A]" : "border-transparent text-slate-500 hover:text-[#0F2A4A]"}`}>
              {t.l}
            </button>
          ))}
        </div>

        {tab === "overview" && stats && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4" data-testid="master-stats">
              <StatCard icon={Briefcase} label="Active Jobs" value={stats.active_jobs} tid="stat-active-jobs" />
              <StatCard icon={TrendingUp} label="Total Applications" value={stats.total_applications} tid="stat-total-apps" />
              <StatCard icon={Star} label="Shortlisted" value={stats.shortlisted} tid="stat-shortlisted" />
              <StatCard icon={CalendarCheck} label="Interviews Scheduled" value={stats.interviews} tid="stat-interviews" />
              <StatCard icon={Trophy} label="Selected" value={stats.selected} tid="stat-selected" />
              <StatCard icon={Award} label="Joined" value={stats.joined} tid="stat-joined" />
              <StatCard icon={PauseCircle} label="Jobs on Hold" value={stats.jobs_on_hold} tid="stat-jobs-hold" />
              <StatCard icon={XCircle} label="Closed Jobs" value={stats.closed_jobs} tid="stat-jobs-closed" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
              <MiniStat label="Candidates in DB" value={stats.total_candidates} tid="stat-candidates" />
              <MiniStat label="Client Submissions" value={stats.client_submissions} tid="stat-client-subs" />
              <MiniStat label="CV Submissions" value={stats.cv_submissions} tid="stat-cv-subs" />
              <MiniStat label="Hiring Requirements" value={stats.hiring_requests} tid="stat-hiring" />
              <MiniStat label="Contact Leads" value={stats.contacts} tid="stat-contacts" />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <div className="ae-card p-6">
                <h3 className="font-heading font-medium text-[#0F2A4A] mb-4">Recruitment Funnel</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={funnelData}>
                    <XAxis dataKey="name" style={{ fontSize: 12 }} />
                    <YAxis style={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#C5A059" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="ae-card p-6">
                <h3 className="font-heading font-medium text-[#0F2A4A] mb-4">Status Distribution</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={90} label>
                      {pieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}

        {tab === "pipeline" && (
          <div className="ae-kanban">
            {STAGES.map(stage => {
              const items = apps.filter(a => a.status === stage.k);
              return (
                <div key={stage.k} className={`min-w-[280px] max-w-[300px] rounded-xl p-3 border ${stage.color}`} data-testid={`kanban-col-${stage.k}`}>
                  <div className="flex justify-between items-center mb-3 px-1">
                    <h4 className="font-heading font-medium text-[#0F2A4A] text-sm">{stage.l}</h4>
                    <span className="text-xs font-semibold text-slate-500 bg-white px-2 py-0.5 rounded-full">{items.length}</span>
                  </div>
                  <div className="space-y-2">
                    {items.slice(0, 20).map(a => {
                      const resumeUrl = a.resume_path ? fileUrl(a.resume_path) : null;
                      return (
                        <div key={a.id} className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm hover:border-[#C5A059] transition-colors" data-testid={`kanban-card-${a.id}`}>
                          <button onClick={() => a.profile_id && setProfileId(a.profile_id)} data-testid={`kanban-name-${a.id}`}
                            className="font-medium text-sm text-[#0F2A4A] hover:text-[#C5A059] text-left">{a.candidate_name}</button>
                          <div className="text-xs text-slate-500 mt-0.5 truncate">{a.job_title}</div>
                          <div className="text-xs text-slate-400 mt-1">{a.candidate_email}</div>
                          {a.interview_at && (
                            <div className="mt-1 text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-700 inline-block">📅 {new Date(a.interview_at).toLocaleString()}</div>
                          )}
                          <div className="mt-2 flex gap-1 flex-wrap">
                            {resumeUrl && (
                              <button onClick={() => setPreviewPdf({ url: resumeUrl, name: a.candidate_name })}
                                data-testid={`kanban-preview-${a.id}`}
                                className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 hover:bg-purple-200">
                                👁 Resume
                              </button>
                            )}
                            <button onClick={() => { setInterviewFor(a.id); setInterviewAt(""); }}
                              data-testid={`kanban-schedule-${a.id}`}
                              className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200">
                              📅 Interview
                            </button>
                            {a.submitted_to_client ? (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid={`kanban-sent-${a.id}`}>Sent to client</span>
                            ) : (
                              <button onClick={() => setSubmitFor(a)} data-testid={`kanban-submit-client-${a.id}`}
                                className="text-[10px] px-2 py-0.5 rounded-full bg-[#0F2A4A] text-white hover:bg-[#1E3A8A] inline-flex items-center gap-1">
                                <Send className="w-2.5 h-2.5" /> To Client
                              </button>
                            )}
                          </div>
                          <select value="" onChange={e => e.target.value && updateStatus(a.id, e.target.value)} data-testid={`move-select-${a.id}`}
                            className="mt-2 w-full text-[11px] rounded-md border border-slate-200 bg-slate-50 px-2 py-1 outline-none focus:border-[#C5A059]">
                            <option value="">Move to…</option>
                            {STAGES.filter(s => s.k !== stage.k).map(s => <option key={s.k} value={s.k}>{s.l}</option>)}
                          </select>
                        </div>
                      );
                    })}
                    {items.length === 0 && <div className="text-xs text-slate-400 text-center py-4">Empty</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === "candidates" && (
          <div className="space-y-4">
            <div className="ae-card p-4 flex gap-2 flex-wrap">
              <div className="flex-1 min-w-[200px] flex items-center gap-2 px-3 rounded-xl border border-slate-200 bg-slate-50">
                <Search className="w-4 h-4 text-slate-400" />
                <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && searchCands()}
                  data-testid="cand-search"
                  placeholder="Search name, skills, headline…"
                  className="flex-1 py-2 bg-transparent outline-none text-sm" />
              </div>
              <button onClick={() => searchCands()} className="ae-btn-primary text-sm" data-testid="cand-search-btn">Search</button>
              <select value={candFilters.source} onChange={e => setCandFilter("source", e.target.value)} data-testid="cand-filter-source"
                className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs outline-none">
                <option value="">All sources</option>
                {[{ v: "registered", l: "Website Signup" }, { v: "cv_submission", l: "CV Submission" }, ...CANDIDATE_SOURCES].map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
              <select value={candFilters.industry} onChange={e => setCandFilter("industry", e.target.value)} data-testid="cand-filter-industry"
                className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs outline-none">
                <option value="">All industries</option>
                {INDUSTRY_NAMES.map(i => <option key={i}>{i}</option>)}
              </select>
              <button onClick={() => setAddCand(true)} className="ae-btn-secondary text-sm inline-flex items-center gap-2" data-testid="add-candidate-btn">
                <UserPlus className="w-4 h-4" /> Add Candidate
              </button>
              <button onClick={downloadZip} disabled={selectedCands.length === 0} data-testid="download-zip-btn"
                className={`text-sm px-4 py-2 rounded-xl font-medium transition-all inline-flex items-center gap-2 ${selectedCands.length ? "bg-purple-600 text-white hover:bg-purple-700" : "bg-slate-200 text-slate-400 cursor-not-allowed"}`}>
                📦 Download {selectedCands.length > 0 ? `${selectedCands.length} ` : ""}Resumes as ZIP
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {candidates.map(c => <CandidateCard key={c.id} c={c}
                reload={() => searchCands()}
                selected={selectedCands.includes(c.id)}
                onToggle={() => toggleCand(c.id)}
                onOpen={() => setProfileId(c.id)}
                onPreview={setPreviewPdf} />)}
              {candidates.length === 0 && <div className="ae-card p-8 text-center text-slate-500 col-span-2">No candidates yet.</div>}
            </div>
          </div>
        )}

        {tab === "jobs" && <JobsManager jobs={jobs} reload={load} />}

        {tab === "team" && user.role === "admin" && <TeamAccess />}

        {tab === "submissions" && <ClientSubmissions jobs={jobs} onOpenProfile={setProfileId} refreshKey={subsKey} />}

        {tab === "leads" && (
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-heading font-medium text-[#0F2A4A] mb-3">Contact Enquiries ({contacts.length})</h3>
              <div className="space-y-2">
                {contacts.map(c => {
                  const mail = `mailto:${c.email}?subject=${encodeURIComponent("Re: " + c.subject + " — Achievers Enterprise")}&body=${encodeURIComponent(`Hi ${c.name.split(" ")[0]},\n\nThank you for reaching out to Achievers Enterprise. We've received your enquiry regarding "${c.subject}" and would like to discuss it further.\n\nCould you share a convenient time for a quick call? You can also reach us directly at:\n📞 +91 90990 42472\n💼 HR.kuldipsinh@achieversenterprise.in\n\nWarm regards,\nAchievers Enterprise Team\nConnecting Talent with Opportunities`)}`;
                  const wa = c.phone ? `https://wa.me/${c.phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Hi ${c.name.split(" ")[0]}, this is Achievers Enterprise. Thanks for your enquiry regarding "${c.subject}". When would be a good time for a quick call?`)}` : null;
                  return (
                    <div key={c.id} className="ae-card p-4" data-testid={`contact-${c.id}`}>
                      <div className="font-medium text-[#0F2A4A]">{c.name}</div>
                      <div className="text-xs text-slate-500">{c.email}{c.phone && ` · ${c.phone}`}</div>
                      <div className="text-sm mt-2 font-medium">{c.subject}</div>
                      <div className="text-xs text-slate-600 mt-1">{c.message}</div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <a href={mail} data-testid={`reply-email-${c.id}`} className="text-xs px-3 py-1.5 rounded-lg bg-[#0F2A4A] text-white hover:bg-[#1E3A8A] transition-colors inline-flex items-center gap-1.5">
                          <Mail className="w-3 h-3" /> Reply by Email
                        </a>
                        {wa && (
                          <a href={wa} target="_blank" rel="noreferrer" data-testid={`reply-wa-${c.id}`} className="text-xs px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors inline-flex items-center gap-1.5">
                            <MessageCircle className="w-3 h-3" /> WhatsApp
                          </a>
                        )}
                        {c.phone && (
                          <a href={`tel:${c.phone}`} data-testid={`reply-call-${c.id}`} className="text-xs px-3 py-1.5 rounded-lg bg-[#C5A059] text-white hover:bg-[#D4AF37] transition-colors inline-flex items-center gap-1.5">
                            <Phone className="w-3 h-3" /> Call
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
                {contacts.length === 0 && <div className="ae-card p-6 text-center text-slate-500">No enquiries yet.</div>}
              </div>
            </div>
            <div>
              <h3 className="font-heading font-medium text-[#0F2A4A] mb-3">Hiring Requirements ({hiringReqs.length})</h3>
              <div className="space-y-2">
                {hiringReqs.map(h => {
                  const mail = `mailto:${h.email}?subject=${encodeURIComponent(`Re: Hiring Requirement — ${h.positions} at ${h.company_name}`)}&body=${encodeURIComponent(`Hi ${h.contact_name.split(" ")[0]},\n\nThank you for choosing Achievers Enterprise for your hiring needs at ${h.company_name}. We understand you're looking for ${h.positions} in ${h.industry} with ${h.urgency.toLowerCase()} priority.\n\nOur team would like to schedule a quick call to understand your requirement in detail — job description, budget, and timelines. Could you share a convenient time?\n\nMeanwhile, feel free to reach us directly:\n📞 +91 90990 42472\n💼 HR.kuldipsinh@achieversenterprise.in\n\nWe typically send the first shortlist within 48 hours.\n\nWarm regards,\nAchievers Enterprise\nConnecting Talent with Opportunities`)}`;
                  const wa = `https://wa.me/${h.phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Hi ${h.contact_name.split(" ")[0]}, this is Achievers Enterprise. Thanks for sharing your hiring requirement for ${h.positions} at ${h.company_name}. When can we schedule a quick call to discuss details?`)}`;
                  return (
                    <div key={h.id} className="ae-card p-4" data-testid={`hiring-${h.id}`}>
                      <div className="font-medium text-[#0F2A4A]">{h.company_name}</div>
                      <div className="text-xs text-slate-500">{h.contact_name} · {h.email} · {h.phone}</div>
                      <div className="text-sm mt-2">{h.positions} · {h.industry} · <span className="text-[#C5A059] font-medium">{h.urgency}</span></div>
                      {h.details && <div className="text-xs text-slate-600 mt-1">{h.details}</div>}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <a href={mail} data-testid={`hr-reply-email-${h.id}`} className="text-xs px-3 py-1.5 rounded-lg bg-[#0F2A4A] text-white hover:bg-[#1E3A8A] transition-colors inline-flex items-center gap-1.5">
                          <Mail className="w-3 h-3" /> Reply by Email
                        </a>
                        <a href={wa} target="_blank" rel="noreferrer" data-testid={`hr-reply-wa-${h.id}`} className="text-xs px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors inline-flex items-center gap-1.5">
                          <MessageCircle className="w-3 h-3" /> WhatsApp
                        </a>
                        <a href={`tel:${h.phone}`} data-testid={`hr-reply-call-${h.id}`} className="text-xs px-3 py-1.5 rounded-lg bg-[#C5A059] text-white hover:bg-[#D4AF37] transition-colors inline-flex items-center gap-1.5">
                          <Phone className="w-3 h-3" /> Call
                        </a>
                      </div>
                    </div>
                  );
                })}
                {hiringReqs.length === 0 && <div className="ae-card p-6 text-center text-slate-500">No hiring requests yet.</div>}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* PDF Preview Modal */}
      {previewPdf && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setPreviewPdf(null)} data-testid="pdf-preview-modal">
          <div className="bg-white rounded-2xl max-w-5xl w-full h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-100 flex justify-between items-center">
              <div>
                <h3 className="font-heading font-medium text-[#0F2A4A]">📄 Resume — {previewPdf.name}</h3>
              </div>
              <div className="flex gap-2">
                <a href={previewPdf.url} download target="_blank" rel="noreferrer" data-testid="preview-download-btn"
                  className="text-xs px-3 py-1.5 rounded-lg bg-purple-600 text-white hover:bg-purple-700">⬇ Download</a>
                <button onClick={() => setPreviewPdf(null)} data-testid="preview-close-btn"
                  className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200">✕ Close</button>
              </div>
            </div>
            <iframe src={previewPdf.url} title="Resume Preview" className="flex-1 w-full border-0" />
          </div>
        </div>
      )}

      {addCand && <AddCandidateModal jobs={jobs} onClose={() => setAddCand(false)}
        onSaved={(id) => { setAddCand(false); refreshAll(); setProfileId(id); }} />}
      {profileId && <CandidateProfileModal pid={profileId} jobs={jobs} onClose={() => setProfileId(null)}
        onChanged={refreshAll} onPreview={setPreviewPdf} />}
      {submitFor && <SubmitToClientModal application={submitFor} onClose={() => setSubmitFor(null)}
        onSaved={() => { setSubmitFor(null); refreshAll(); }} />}

      {/* Interview Scheduling Modal */}
      {interviewFor && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setInterviewFor(null)} data-testid="interview-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading font-medium text-xl text-[#0F2A4A] mb-4">📅 Schedule Interview</h3>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Interview Date & Time</label>
            <input type="datetime-local" value={interviewAt} onChange={e => setInterviewAt(e.target.value)}
              data-testid="interview-datetime"
              className="w-full rounded-xl border border-slate-200 bg-slate-50 outline-none focus:border-[#C5A059] px-4 py-3 text-sm" />
            <div className="flex gap-2 mt-6">
              <button onClick={() => setInterviewFor(null)} className="ae-btn-outline text-sm flex-1">Cancel</button>
              <button onClick={scheduleInterview} className="ae-btn-primary text-sm flex-1" data-testid="interview-save-btn">Schedule & Notify</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const MiniStat = ({ label, value, tid }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-center justify-between" data-testid={tid}>
    <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
    <span className="font-heading text-lg font-semibold text-[#0F2A4A]">{value ?? 0}</span>
  </div>
);

const StatCard = ({ icon: Icon, label, value, tid }) => (
  <div className="ae-card p-5" data-testid={tid}>
    <div className="flex items-center justify-between mb-2">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <Icon className="w-4 h-4 text-[#C5A059]" />
    </div>
    <div className="font-heading text-3xl font-semibold text-[#0F2A4A]">{value ?? 0}</div>
  </div>
);

function CandidateCard({ c, reload, selected, onToggle, onPreview, onOpen }) {
  const [showNotes, setShowNotes] = React.useState(false);
  const [notes, setNotes] = React.useState(c.recruiter_notes || "");
  const [saving, setSaving] = React.useState(false);
  const rawDigits = (c.phone || "").replace(/\D/g, "");
  const phoneDigits = rawDigits.length === 10 ? `91${rawDigits}` : rawDigits;
  const resumeUrl = c.resume_path ? fileUrl(c.resume_path) : null;

  const saveNotes = async () => {
    setSaving(true);
    try {
      await api.patch(`/candidates/${c.id}/notes`, { recruiter_notes: notes });
      toast.success("Notes saved");
      reload();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <div className={`ae-card p-5 ${selected ? "ring-2 ring-[#C5A059]" : ""}`} data-testid={`cand-${c.id}`}>
      <div className="flex justify-between items-start">
        <div className="flex gap-2 items-start">
          {onToggle && (
            <input type="checkbox" checked={selected} onChange={onToggle}
              data-testid={`cand-select-${c.id}`}
              className="mt-1 w-4 h-4 accent-[#C5A059] cursor-pointer" />
          )}
          <div>
            <button onClick={onOpen} data-testid={`cand-open-${c.id}`} className="font-heading font-medium text-[#0F2A4A] hover:text-[#C5A059] text-left">{c.full_name}</button>
            <div className="text-xs text-slate-500">{c.headline || c.preferred_job || "—"}</div>
            <div className="flex flex-wrap gap-1 mt-1">
              {(c.sources || []).map(s => <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-[#C5A059]/15 text-[#0F2A4A]">{SOURCE_LABELS[s] || s}</span>)}
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600" data-testid={`cand-appcount-${c.id}`}>{c.applications_count || 0} applications</span>
            </div>
          </div>
        </div>
        {c.total_experience > 0 && <span className="text-xs bg-[#C5A059]/10 text-[#0F2A4A] px-2 py-1 rounded-full">{c.total_experience} yrs</span>}
      </div>
      <div className="mt-3 text-xs text-slate-600 space-y-1">
        {c.email && <div>✉ {c.email}</div>}
        {c.phone && <div>📱 {c.phone}</div>}
        {c.current_location && <div>📍 {c.current_location}</div>}
        {c.expected_salary > 0 && <div>💰 Expected ₹{(c.expected_salary/100000).toFixed(1)} LPA</div>}
        {c.notice_period && <div>⏱ Notice: {c.notice_period}</div>}
      </div>
      {c.skills?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {c.skills.slice(0, 6).map(s => <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{s}</span>)}
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        {c.email && (
          <a href={`mailto:${c.email}?subject=${encodeURIComponent("Opportunity from Achievers Enterprise")}&body=${encodeURIComponent(`Hi ${(c.full_name || "").split(" ")[0]},\n\nI'm reaching out from Achievers Enterprise regarding an opportunity that matches your profile.\n\nAre you available for a quick call this week?\n\nBest regards,\nAchievers Enterprise\n+91 90990 42472`)}`}
            data-testid={`cand-email-${c.id}`}
            className="text-xs px-3 py-1.5 rounded-lg bg-[#0F2A4A] text-white hover:bg-[#1E3A8A] inline-flex items-center gap-1.5">
            <Mail className="w-3 h-3" /> Email
          </a>
        )}
        {phoneDigits && (
          <a href={`https://wa.me/${phoneDigits}?text=${encodeURIComponent(`Hi ${(c.full_name || "").split(" ")[0]}, this is Achievers Enterprise. We have an opportunity matching your profile. When can we chat?`)}`}
            target="_blank" rel="noreferrer" data-testid={`cand-wa-${c.id}`}
            className="text-xs px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1.5">
            <MessageCircle className="w-3 h-3" /> WhatsApp
          </a>
        )}
        {c.phone && (
          <a href={`tel:${c.phone}`} data-testid={`cand-call-${c.id}`}
            className="text-xs px-3 py-1.5 rounded-lg bg-[#C5A059] text-white hover:bg-[#D4AF37] inline-flex items-center gap-1.5">
            <Phone className="w-3 h-3" /> Call
          </a>
        )}
        {resumeUrl && (
          <>
            <button onClick={() => onPreview && onPreview({ url: resumeUrl, name: c.full_name })}
              data-testid={`cand-preview-${c.id}`}
              className="text-xs px-3 py-1.5 rounded-lg bg-purple-600 text-white hover:bg-purple-700 inline-flex items-center gap-1.5">
              👁 Preview Resume
            </button>
            <a href={resumeUrl} target="_blank" rel="noreferrer" download data-testid={`cand-resume-${c.id}`}
              className="text-xs px-3 py-1.5 rounded-lg border border-purple-600 text-purple-700 hover:bg-purple-50 inline-flex items-center gap-1.5">
              ⬇ Download
            </a>
          </>
        )}
        <button onClick={onOpen} data-testid={`cand-view-${c.id}`}
          className="text-xs px-3 py-1.5 rounded-lg border border-[#0F2A4A] text-[#0F2A4A] hover:bg-[#0F2A4A] hover:text-white inline-flex items-center gap-1.5 transition-colors">
          <Eye className="w-3 h-3" /> View Profile
        </button>
        <button onClick={() => setShowNotes(!showNotes)} data-testid={`cand-notes-toggle-${c.id}`}
          className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5">
          📝 Notes {c.recruiter_notes ? "•" : ""}
        </button>
      </div>
      {showNotes && (
        <div className="mt-3 space-y-2">
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
            data-testid={`cand-notes-input-${c.id}`}
            placeholder="Internal recruiter notes — interview feedback, follow-up reminders, etc."
            className="w-full rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#C5A059] outline-none px-3 py-2 text-xs" />
          <div className="flex justify-between items-center">
            <div className="text-[10px] text-slate-400">{c.notes_updated_by ? `Last updated by ${c.notes_updated_by}` : ""}</div>
            <button onClick={saveNotes} disabled={saving} data-testid={`cand-notes-save-${c.id}`}
              className="text-xs px-3 py-1 rounded-lg bg-[#0F2A4A] text-white hover:bg-[#1E3A8A]">
              {saving ? "Saving…" : "Save Notes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
